import { resolveStepAndLessonImages } from './imageService';
import { validateAndPaginateAllSteps, normalizeBoardContentLines } from './layoutEngine';
import { cleanCaptionText, cleanForSpeech } from './latexUtils';
import { isValidRenderableImageUrl } from './visualUtils';

export type Step = {
  step: number;
  board_content: string;
  reference_text?: string;
  explanation: string;
  audio_explanation?: string;
  diagram_svg?: string;
  diagram_description?: string;
  image_query?: string;
  image_url?: string;
  static_image_url?: string;
  content_type?: 'text' | 'heading' | 'equation' | 'aligned_equation' | 'matrix' | 'determinant' | 'table' | 'data_table' | 'comparison' | 'vector' | 'derivation' | 'definition';
  structured_table?: {
    headers?: string[];
    rows: string[][];
  };
  inline_diagram_svg?: string;
};

export type AIOutput = {
  subject: string;
  topic: string;
  question: string;
  steps: Step[];
  formulas: string[];
  diagram_needed: boolean;
  visual_type: "DYNAMIC" | "STATIC" | "NONE";
  static_image_query?: string;
  static_image_url?: string;
  image_keywords?: string[];
  diagram_description?: string;
  final_answer?: string;
  is_doubt_mode?: boolean;
};

function formatContentsForRestApi(contents: any): any[] {
  if (!contents) return [];
  const list = Array.isArray(contents) ? contents : [contents];
  return list.map((item: any) => {
    if (typeof item === 'string') {
      return { role: 'user', parts: [{ text: item }] };
    }
    if (item && item.parts && Array.isArray(item.parts)) {
      const parts = item.parts.map((p: any) => {
        if (typeof p === 'string') {
          return { text: p };
        }
        if (p && p.inlineData) {
          return {
            inline_data: {
              mime_type: p.inlineData.mimeType || p.inlineData.mime_type || 'image/jpeg',
              data: p.inlineData.data
            }
          };
        }
        if (p && p.inline_data) {
          return {
            inline_data: {
              mime_type: p.inline_data.mime_type || p.inline_data.mimeType || 'image/jpeg',
              data: p.inline_data.data
            }
          };
        }
        return p;
      });
      return { role: item.role || 'user', parts };
    }
    if (item && item.text) {
      return { role: 'user', parts: [{ text: item.text }] };
    }
    return item;
  });
}

async function callGemini(
  modelName: string,
  contents: any[],
  systemInstruction?: string,
  responseMimeType?: string
) {
  try {
    const googleToken = typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null;
    const userApiKey = typeof window !== 'undefined' ? localStorage.getItem('gemini_api_key') : null;
    const sessionId = typeof window !== 'undefined' ? localStorage.getItem('google_session_id') : null;

    const executeFetch = async () => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (userApiKey && userApiKey.trim()) {
        headers["x-gemini-api-key"] = userApiKey.trim();
      }
      if (googleToken && googleToken.trim()) {
        headers["Authorization"] = `Bearer ${googleToken.trim()}`;
        headers["x-google-access-token"] = googleToken.trim();
        headers["x-goog-user-project"] = "professor-x-visuals";
      }
      if (sessionId && sessionId.trim()) {
        headers["x-session-id"] = sessionId.trim();
      }

      return await fetch("/api/gemini/generate", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ contents, systemInstruction, responseMimeType }),
      });
    };

    let response = await executeFetch();

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status}`;
      try {
        const errorData = await response.json();
        const rawErrStr = errorData.error ? (typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error)) : "";
        const lowerErr = rawErrStr.toLowerCase();
        if (
          response.status === 401 ||
          lowerErr.includes("invalid authentication credentials") ||
          lowerErr.includes("expected oauth 2 access token") ||
          rawErrStr.includes("UNAUTHENTICATED")
        ) {
          errorMessage = "Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account.";
        } else if (
          rawErrStr.includes("currently experiencing high demand") || 
          rawErrStr.includes("UNAVAILABLE") || 
          rawErrStr.includes("503") ||
          rawErrStr.includes("currently busy")
        ) {
          errorMessage = "our server is currently busy try soon";
        } else {
          errorMessage = errorData.error || errorMessage;
        }
      } catch (e) {
        // Fallback to text if JSON parsing fails
        try {
          const text = await response.text();
          if (text) {
            const lowerText = text.toLowerCase();
            if (
              response.status === 401 ||
              lowerText.includes("invalid authentication credentials") ||
              lowerText.includes("expected oauth 2 access token") ||
              text.includes("UNAUTHENTICATED")
            ) {
              errorMessage = "Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account.";
            } else if (
              text.includes("currently experiencing high demand") || 
              text.includes("UNAVAILABLE") || 
              text.includes("503") ||
              text.includes("currently busy")
            ) {
              errorMessage = "our server is currently busy try soon";
            } else if (text.includes("<!DOCTYPE html>") || text.includes("<html")) {
              errorMessage += " (Server returned an error page)";
            } else {
              errorMessage += ` - ${text.substring(0, 100)}`;
            }
          }
        } catch (textErr) {
          // Ignore
        }
      }
      throw new Error(errorMessage);
    }

    try {
      return await response.json();
    } catch (parseErr) {
      throw new Error("Failed to parse response from server as JSON");
    }
  } catch (error: any) {
    console.error("Call Gemini Error:", error);
    throw error;
  }
}

export function repairJsonString(jsonStr: string): string {
  let result = '';
  let inString = false;
  let isEscaped = false;
  
  for (let i = 0; i < jsonStr.length; i++) {
    const char = jsonStr[i];
    
    if (inString) {
      if (isEscaped) {
        // Valid string escapes to keep as-is (n, r, ", \, u)
        let isValidEscape = false;
        if (['"', '\\', 'n', 'r'].includes(char)) {
          isValidEscape = true;
        } else if (char === 'u') {
          const nextFour = jsonStr.substring(i + 1, i + 5);
          if (/^[0-9a-fA-F]{4}$/.test(nextFour)) {
            isValidEscape = true;
          }
        }
        
        if (isValidEscape) {
          result += '\\' + char;
        } else {
          // Doubled backslash to preserve LaTeX backslashes like \theta, \frac, \beta
          result += '\\\\' + char;
        }
        isEscaped = false;
      } else if (char === '\\') {
        isEscaped = true;
      } else if (char === '"') {
        inString = false;
        result += char;
      } else if (char === '\n') {
        result += '\\n';
      } else if (char === '\r') {
        result += '\\r';
      } else if (char === '\t') {
        result += '\\t';
      } else {
        result += char;
      }
    } else {
      if (char === '"') {
        inString = true;
        result += char;
      } else {
        result += char;
      }
    }
  }
  
  if (inString) {
    if (isEscaped) {
      result += '\\\\';
    }
    result += '"';
  }
  
  return result;
}

export function validateAndSanitizeVideoData<T extends AIOutput>(data: T): T {
  if (!data) return data;

  const sanitizeStr = (str: string | undefined | null, fallback: string = ''): string => {
    if (!str) return fallback;
    let s = String(str).trim();

    // 1. Remove explicit string literal corruptions / undefined values / null representations
    s = s.replace(/\b(null|undefined|\[object Object\]|NaN|Infinity)\b/gi, '');

    // 2. Remove isolated single-letter orphan lines or garbage fragments like "a", "b", "x" alone
    s = s.replace(/^([a-zA-Z])\s*[\.\,\;\:\-]*$/g, '');
    s = s.replace(/\s+([a-zA-Z])\s*[\.\,]\s+/g, ' ');

    // 3. Clean up dangling punctuation
    s = s.replace(/[\s,.-]+(?=[\s,.-]*$)/g, '');
    s = s.replace(/^\s*[,.-]+\s*/g, '');
    s = s.replace(/\s*,\s*,+/g, ',');
    s = s.replace(/\s*\.\s*\.+/g, '.');
    s = s.replace(/\s*,\s*\./g, '.');
    s = s.replace(/\s+([,.:;?!])/g, '$1');

    // 4. Clean up whitespace
    s = s.replace(/\s{2,}/g, ' ').trim();

    return s || fallback;
  };

  const latexToSpokenText = (text: string): string => {
    if (!text) return '';
    let t = text.replace(/\$\$/g, '').replace(/\$/g, '');
    t = t.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 over $2');
    t = t.replace(/\\sqrt\{([^}]+)\}/g, 'square root of $1');
    t = t.replace(/\\theta/g, 'theta');
    t = t.replace(/\\alpha/g, 'alpha');
    t = t.replace(/\\beta/g, 'beta');
    t = t.replace(/\\pi/g, 'pi');
    t = t.replace(/\\times/g, 'times');
    t = t.replace(/\\div/g, 'divided by');
    t = t.replace(/\\pm/g, 'plus or minus');
    t = t.replace(/\\[\w]+/g, ' ');
    t = t.replace(/[\{\}]/g, '');
    return sanitizeStr(t);
  };

  const sanitizedSubject = sanitizeStr(data.subject, 'General');
  const sanitizedTopic = sanitizeStr(data.topic, 'Educational Topic');
  const sanitizedQuestion = sanitizeStr(data.question, 'Question');
  const sanitizedFinalAnswer = sanitizeStr(data.final_answer);
  const sanitizedDiagramDesc = sanitizeStr(data.diagram_description);

  let rawSteps = Array.isArray(data.steps) ? [...data.steps] : [];
  rawSteps = rawSteps.filter(s => s && typeof s === 'object');

  if (rawSteps.length === 0) {
    rawSteps.push({
      step: 1,
      board_content: sanitizedQuestion || "Welcome to the lesson.",
      explanation: "Hello, I am Professor X. Let's work through this question together step-by-step.",
      audio_explanation: "Hello, I am Professor X. Let's work through this question together step by step."
    });
  }

  // Preserve complete canonical narration scripts per step and structure board content cleanly
  const cleanSteps: Step[] = rawSteps.map((s, idx) => {
    const stepNum = idx + 1;

    let rawBoard = typeof s.board_content === 'string' ? s.board_content : '';
    const singleDollarCount = (rawBoard.match(/(?<!\$)\$(?!\$)/g) || []).length;
    if (singleDollarCount % 2 !== 0) rawBoard += '$';
    const doubleDollarCount = (rawBoard.match(/\$\$/g) || []).length;
    if (doubleDollarCount % 2 !== 0) rawBoard += '$$';
    let cleanBoard = sanitizeStr(rawBoard);

    let cleanExp = sanitizeStr(s.explanation);
    let cleanAudio = sanitizeStr(s.audio_explanation);

    // If explanation is present but audio is missing (or vice versa), align them to canonical narration
    if (!cleanExp || cleanExp.length < 4) {
      if (cleanAudio && cleanAudio.length >= 4) {
        cleanExp = cleanAudio;
      } else {
        const spoken = latexToSpokenText(cleanBoard);
        cleanExp = spoken 
          ? `In this step, we analyze: ${spoken}.` 
          : `Let's examine step ${stepNum} of the problem.`;
      }
    }

    if (!cleanAudio || cleanAudio.length < 4) {
      cleanAudio = cleanExp;
    }

    // Clean explanation and audio explanation to remove raw LaTeX backslashes, $, and control artifacts
    cleanExp = cleanCaptionText(cleanExp, 'English');
    cleanAudio = cleanCaptionText(cleanAudio, (data as any)?.language || 'English');

    // Standardize board content to be concise, structured visual teaching material
    cleanBoard = ensureStructuredBoardContent(cleanBoard, cleanExp || cleanAudio, sanitizedTopic);

    if (stepNum === 1) {
      // Enforce exact "Hello, I am Professor X" identity rule for Step 1 intro in English
      cleanExp = cleanExp.replace(/\b(Hello,?\s+)?I\s+am\s+Professor\s+today\b/gi, 'Hello, I am Professor X, today');
      cleanExp = cleanExp.replace(/\b(Hello,?\s+)?I\s+am\s+Professor\b(?!\s+X)/gi, 'Hello, I am Professor X');

      // Preserve English brand name "Professor X" across Indic variations
      cleanAudio = cleanAudio.replace(/프로फेसर\s*(एक्स|X|x)/gi, 'Professor X');
      cleanAudio = cleanAudio.replace(/प्राध्यापक\s*(एक्स|X|x)/gi, 'Professor X');
      cleanAudio = cleanAudio.replace(/\b(Hello,?\s+)?I\s+am\s+Professor\s+today\b/gi, 'Hello, I am Professor X, today');
      cleanAudio = cleanAudio.replace(/\b(Hello,?\s+)?I\s+am\s+Professor\b(?!\s+X)/gi, 'Hello, I am Professor X');
    }

    const cleanRef = sanitizeStr(s.reference_text);
    const cleanStepDiagDesc = sanitizeStr(s.diagram_description);
    const cleanSvg = typeof s.diagram_svg === 'string' && s.diagram_svg.includes('<svg') ? s.diagram_svg : undefined;
    const cleanImageQuery = typeof s.image_query === 'string' && s.image_query.trim() ? sanitizeStr(s.image_query) : (typeof (s as any).static_image_query === 'string' ? sanitizeStr((s as any).static_image_query) : undefined);
    const rawImg = s.image_url || (s as any).static_image_url;
    const cleanImageUrl = isValidRenderableImageUrl(rawImg) ? rawImg : undefined;

    return {
      step: stepNum,
      board_content: cleanBoard,
      explanation: cleanExp,
      audio_explanation: cleanAudio,
      reference_text: cleanRef || undefined,
      diagram_svg: cleanSvg,
      diagram_description: cleanStepDiagDesc || undefined,
      image_query: cleanImageQuery || undefined,
      image_url: cleanImageUrl || undefined,
      static_image_url: cleanImageUrl || undefined
    };
  });

  const cleanLessonImg = isValidRenderableImageUrl(data.static_image_url) ? data.static_image_url : undefined;
  const hasAnyVisual = !!data.diagram_needed || !!cleanLessonImg || cleanSteps.some(s => !!s.diagram_svg || !!s.image_url || !!s.static_image_url || !!s.image_query);

  return {
    ...data,
    subject: sanitizedSubject,
    topic: sanitizedTopic,
    question: sanitizedQuestion,
    diagram_needed: hasAnyVisual,
    static_image_url: cleanLessonImg,
    final_answer: sanitizedFinalAnswer || undefined,
    diagram_description: sanitizedDiagramDesc || undefined,
    steps: cleanSteps,
    formulas: Array.isArray(data.formulas) ? data.formulas.map(f => sanitizeStr(f)).filter(Boolean) : []
  };
}

export function ensureStructuredBoardContent(boardContent: string, audioExplanation: string, title?: string): string {
  let content = (boardContent || '').trim();

  // Strip accidental markdown fence blocks
  content = content.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '').trim();

  if (!content || content.toLowerCase() === 'welcome to the lesson.' || content.length < 5) {
    const headline = title ? `WELCOME TO ${title.toUpperCase().slice(0, 45)}` : 'LESSON INTRODUCTION';
    return `${headline}\n• Problem Statement & Overview`;
  }

  const lines = normalizeBoardContentLines(content);
  return lines.join('\n');
}

export function prepareVideoData(data: AIOutput): AIOutput {
  if (!data || !data.steps || !Array.isArray(data.steps)) return data;

  const validatedData = validateAndSanitizeVideoData(data);

  // Deterministically paginate steps to strictly enforce max 3-4 logical teaching steps per frame and safe layout bounds
  const paginatedSteps = validateAndPaginateAllSteps(validatedData.steps);

  const reindexedSteps = paginatedSteps.map((s, idx) => ({
    ...s,
    step: idx + 1
  }));

  return {
    ...validatedData,
    steps: reindexedSteps
  };
}

export function parseAndCleanJSON(rawText: string): AIOutput {
  let text = rawText.trim();
  
  // Extract JSON if wrapped in markdown code blocks
  const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (jsonMatch && jsonMatch[1]) {
    text = jsonMatch[1].trim();
  } else {
    // Robust extraction: find first '{' and corresponding matching '}' or last '}'
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      text = text.substring(firstBrace, lastBrace + 1);
    }
  }
  
  let parsed: AIOutput;

  // First try direct parsing
  try {
    parsed = JSON.parse(text) as AIOutput;
    return prepareVideoData(parsed);
  } catch (initialError) {
    console.warn("Direct JSON parsing failed, running robust repair.", initialError);
  }
  
  // Repair backslashes, control characters, literal line breaks inside strings
  const repaired = repairJsonString(text);
  
  try {
    parsed = JSON.parse(repaired) as AIOutput;
    return prepareVideoData(parsed);
  } catch (repairError) {
    console.warn("Repaired JSON parse failed, trying control character cleanup fallback", repairError);
    // Cleanup control characters and try one more time
    const finalFallback = repaired.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
    try {
      parsed = JSON.parse(finalFallback) as AIOutput;
      return prepareVideoData(parsed);
    } catch (finalError: any) {
      console.error("All JSON parsing attempts failed!");
      throw new Error(`Invalid JSON format from AI: ${finalError.message}`);
    }
  }
}

export async function generateDoubtSolution(doubtText: string, originalQuestion: string, stepsContext: string, language: string = 'English'): Promise<AIOutput> {
  const systemInstruction = `
    You are Professor X. A student has a DOUBT about a question you just solved.
    Your goal is to explain this specific doubt in ${language} for both text and audio.
    
    ORIGINAL QUESTION: ${originalQuestion}
    ORIGINAL STEPS: ${stepsContext}
    STUDENT'S DOUBT: ${doubtText}

    CRITICAL: 
    - Provide a SHORT, structured solution (1-3 NEW steps) specifically addressing the student's doubt.
    - This explanation is for the CHAT INTERFACE ONLY. It will NOT be shown on the main board.
    - DO NOT repeat the entire original solution. Focus exclusively on the part the student is confused about.
    - Each step should have 'board_content' (max 3 lines), 'explanation' (in ${language}), and 'audio_explanation' (in ${language}).
    - Use LaTeX for math.
    - Output MUST be valid JSON matching the AIOutput type and ONLY the JSON object.
    - Set "is_doubt_mode": true.

    TEACHING PHILOSOPHY & DOUBT RESOLUTION ALIGNMENT:
    - Responses must be simple, clear, and step-by-step.
    - Prioritize intuition over complexity and clarity over brevity.
    - Re-explain concepts from basics if needed.
    - Make explanations feel like a real teacher in a classroom: natural flow, logical progression, guided understanding.
    - Transition smoothly with a calm, bold, and professional tone.
    - VISUALS FOR DOUBT RESOLUTION: If the student's doubt genuinely requires a diagram or visual (e.g. clarifying an angle, force direction, or structure), provide 'diagram_svg' (raw SVG string) or 'image_query' directly on that step. If not needed, keep 'diagram_svg': "".
    - CONVERSATIONAL ENGAGEMENT: Dynamically insert short, human-like phrases to make the teacher feel real. 
      Examples: "Got it so far?", "Notice what happened here?", "This part is important.", "Now here’s the interesting part.", "Think about it like this...".
      - Use these phrases intelligently (e.g., every 1-2 steps), not at fixed intervals.
      - Ensure they sound natural in ${language} for 'audio_explanation' (e.g., use culturally appropriate fillers).
    
    JSON STRUCTURE TO FOLLOW EXACTLY (ONLY OUTPUT THIS):
    {
      "subject": "...",
      "topic": "Doubt Resolution",
      "question": "DOUBT_SUMMARY",
      "steps": [
        {
          "step": 1,
          "board_content": "...",
          "explanation": "...",
          "audio_explanation": "...",
          "diagram_svg": "",
          "image_query": "",
          "diagram_description": ""
        }
      ],
      "formulas": [],
      "diagram_needed": false,
      "visual_type": "NONE",
      "is_doubt_mode": true
    }
  `;

  try {
    const response = await callGemini(
      "gemini-3-flash-preview",
      [{ role: 'user', parts: [{ text: `Student Doubt: ${doubtText}\n\nProvide the explanation in the required JSON format.`}] }],
      systemInstruction,
      "application/json"
    );

    return parseAndCleanJSON(response.text || "");
  } catch (error) {
    console.error("Error in generateDoubtSolution:", error);
    throw error;
  }
}

export async function generateSolution(questionText: string, imageBase64?: string, mimeType?: string, language: string = 'English'): Promise<AIOutput> {
  const parts: any[] = [];
  
  if (imageBase64 && mimeType) {
    parts.push({
      inlineData: {
        data: imageBase64,
        mimeType: mimeType,
      },
    });
  }

  if (questionText) {
    parts.push({
      text: `USER QUESTION: ${questionText}`
    });
  }
  
  const systemInstruction = `
    You are Professor X, a world-class Multi-Subject AI Teaching Professor. 
    Your goal is to provide a "Real-Time AI Teaching Ecosystem" experience.
    
    SUBJECTS YOU TEACH:
    - Mathematics (Calculus, Geometry, Algebra, Arithmetics)
    - Physics (Motion, Forces, Circuits, Optics, Waves, Electromagnetism, Thermodynamics)
    - Chemistry (Organic/Inorganic, Reactions, Bonding, Periodic table)
    - Biology (Anatomy, Genetics, Cell biology, Processes)
    - Engineering (Data Structures, Algorithms, Compiler Design, Circuit Design, Mechanical flows)
    - Commerce / CA / Economics (Supply-Demand, Accounts, Auditing processes)
    - Law & Constitution (Legal structures, Case flows)
    - General Science, Theory, & Humanities (History, Geography, Sociology)

    PHYSICS & TECHNICAL SYMBOLS (MANDATORY FOR SVG):
    - When teaching Physics/Electronics, you MUST use standard engineering symbols.
    - RESISTORS: Use a zigzag path (e.g., <path d='M 0 10 L 5 0 L 15 20 L 25 0 L 35 20 L 45 0 L 50 10' .../>).
    - VOLTAGE SOURCES: Use two parallel lines (short thick for negative, long thin for positive) or a circle with +/-.
    - CAPACITORS: Two parallel lines with a small gap.
    - INDUCTORS: A series of semi-circles/coils.
    - VECTORS/FORCES: Clear arrows with 30-degree heads, labeled with variable and magnitude.
    - MEASUREMENT MARKS: Use dimension lines (line with two perpendicular small end-bars) for lengths/distances.
    - GROUND: Three decreasing parallel horizontal lines.

    NEW CAPABILITY: ADAPTIVE TEACHING & DIFFICULTY ASSESSMENT
    You must intelligently analyze the user's question, silently assess its difficulty (Easy, Medium, Hard), and adapt your teaching style accordingly. Assume the user is a BEGINNER by default. Ensure simple language, no unexplained jumps, and justify every important step.

    - EASY QUESTIONS:
      - Approach: Keep the explanation short, clear, and direct. Focus on the key concept and use minimal steps. Avoid over-explaining.
    - MEDIUM QUESTIONS:
      - Approach: Provide a step-by-step solution with clear reasoning between steps. Include brief concept references and state important formulas used.
    - HARD QUESTIONS:
      - Approach: Deliver a detailed, teacher-like explanation. Break the problem down, explain *why* each step is taken, build concepts, state assumptions, highlight visual intuition (if applicable), and provide helpful tips and short reminders of prerequisites.

    NARRATION SPEED & FLOW OPTIMIZATION:
    - Overall Pacing: The narration should be engaging and steady, aiming for efficient delivery (~1.25x faster feel without being rushed).
    - CONCISE INTRODUCTIONS: For Step 1 (Welcome), Step 2 (Given Data), and Step 3 (To Find / To Prove / Goal), keep the 'audio_explanation' very brief and direct. Do not dwell on them; get to the core solution quickly.
    - PROFESSOR IDENTITY & INTRODUCTION MANDATE (STRICT):
      - Your fixed identity and brand name is ALWAYS "Professor X" (in exact English characters).
      - BRAND PRESERVATION: The name "Professor X" must NEVER be transliterated (e.g. do NOT write "प्रोफेसर एक्स" or "प्राध्यापक एक्स") and NEVER translated. It must ALWAYS appear as "Professor X" across ALL languages.
      - At the start of every generated video, Step 1 (Welcome / Intro) 'explanation' and 'audio_explanation' MUST begin with a natural localized greeting followed by "Professor X":
        * English: "Hello, I am Professor X..."
        * Marathi: "नमस्कार, मी Professor X..."
        * Hindi: "नमस्ते, मैं Professor X हूँ..."
        * Bengali: "নমস্কার, আমি Professor X..."
        * Tamil: "வணக்கம், நான் Professor X..."
        * Telugu: "నమస్కారం, నేను Professor X..."
        * Gujarati: "નમસ્તે, હું Professor X છું..."
        * Kannada: "ನಮಸ್ಕಾರ, ನಾನು Professor X..."
        * Malayalam: "നമസ്കാരം, ഞാൻ Professor X..."
        * Other languages: Local greeting + "Professor X".
      - NEVER shorten or replace "Professor X" with "Professor", "Professor today", "today", or any other variation.
    - TECHNICAL ACCURACY & MULTILINGUAL PRECISION:
      - NEVER round, skip, or partially ignore decimals, symbols, or units in 'audio_explanation'. If the board shows "2.5 m/s", the audio must explicitly and accurately reflect "2.5 meters per second" (or its equivalent in the target language).
      - Stay consistent with decimal precision provided in the solution.
      - Scientific notation, fractions, and multi-part units must be natural but precise.
    - PHONETIC HINTS (for audio_explanation): Use words instead of symbols where it helps clarity (e.g., say "plus", "divided by", "square root of").

    EXPLANATION TONE & DELIVERY:
    - Make explanations feel like a real teacher in a classroom.
    - Ensure a natural flow, logical progression, and guided understanding.
    - Avoid robotic or overly compressed explanations.
    - Transition smoothly between concepts with a calm, bold, and encouraging tone.
    - CONVERSATIONAL ENGAGEMENT & HUMAN NARRATION:
      - Dynamically insert short, human-like conversational phrases into both 'explanation' and 'audio_explanation' to feel like a real classroom teacher.
      - These phrases should appear after important steps or periodic logical breaks (e.g., every alternate step).
      - Examples: "Got it so far?", "Notice what happened here?", "This part is important.", "Pretty simple, right?", "Now here’s the interesting part.", "See the pattern?", "Focus on this carefully.", "Think about it like this...", "And this is why it works."
      - DYNAMIC VARIATION: Use different phrases across steps and topics to avoid repetition.
      - MULTILINGUAL NATURALNESS: When writing 'audio_explanation' in ${language}, adapt these phrases contextually to sound like a native local teacher in that language. Use culturally appropriate idioms or conversational fillers.
      - EMOTIONAL TONE: Add slight curiosity, encouragement, or reassurance in the narration flow.
      - DO NOT overuse; avoid adding phrases after every single line. Maintain professional, intelligent behavior.

    EXPLANATION ACCURACY & CONTENT VALIDATION MANDATES (STRICT):
    - ABSOLUTE EXPLANATION RELEVANCE: Every step's 'explanation' and 'audio_explanation' MUST be logically correct, fully coherent, and directly relevant to that step's 'board_content'.
    - NO RANDOM VALUES OR NULLS: Never output random numbers, ungrounded constants, 'null', 'undefined', '[object Object]', or 'NaN' in any field.
    - NO FRAGMENTS OR ISOLATED LETTERS: Do not output single isolated alphabets (e.g. "a", "k") or meaningless text fragments. Every sentence must be a complete, grammatically sound educational thought.
    - EXACT CAPTION & AUDIO SYNCHRONIZATION: The 'explanation' (caption) and 'audio_explanation' (speech script) MUST convey the exact same validated educational content.
    - FORMULA & CALCULATION INTEGRITY: All formulas, variables, and calculations in 'board_content' must strictly match the explanation and given problem data. No step may jump to unstated conclusions or contain mathematical contradictions.

    STRICT PIPELINE SEQUENCE & MULTIPLE RELEVANT VISUALS IN PROPER SEQUENCE:
    Your 'steps' array MUST follow a clean, logical pedagogical flow:
    - Step 1: Welcome / Intro. 'board_content' should welcome the user and introduce the topic. 'explanation' and 'audio_explanation' MUST ALWAYS start with "Hello, I am Professor X...". Keep audio very concise.
    - Step 2: Given Data. 'board_content' MUST list the known parameters, values, equations, or facts given in the problem. Keep audio very concise.
    - Step 3: To Find / To Prove / Goal (MANDATORY ADD-ON IMMEDIATELY AFTER GIVEN DATA): 'board_content' MUST explicitly state what needs to be found, proved, calculated, or the main objective/question asked (e.g., "TO FIND:", "TO PROVE:", or "GOAL:"). 'explanation' and 'audio_explanation' MUST clearly and concisely articulate this goal to the student before beginning the solution.
    - Step 4+: Core Concept Explanations, Logical Sections, Derivations, and Step-by-step Solution.
    
    MULTIPLE RELEVANT VISUALS DECISION RULE:
    Evaluate each logical teaching section/step independently:
    - For each concept, stage, apparatus, derivation step, or example:
      Determine: "Does this specific explanation genuinely need or significantly benefit from a visual?"
      * If YES: Provide a dedicated, highly accurate 'diagram_svg' (for math/physics vectors, graphs, circuits, geometry) or 'image_query' (for biology/chemistry/practical apparatus/real-world science) directly on that step.
      * If NO: Do not force a visual (leave 'diagram_svg': undefined, no 'image_query').
    - Thus, a lesson may have 0 visuals (pure algebraic drill), 1 visual (single focal problem), or MULTIPLE DISTINCT VISUALS in sequence if different concepts/stages genuinely require them (e.g. Concept 1 has Visual 1, followed by calculations, then Concept 2 has Visual 2, followed by conclusions).
    - QUALITY OVER QUANTITY:
      * Do NOT add random, decorative, or repetitive visuals.
      * Do NOT repeat the exact same diagram unless the later step genuinely re-analyzes a modified state.
      * Every visual must maintain the highest standards of scientific accuracy, crisp scales, and proper labels.

    VISUAL REPRESENTATION RULES (STRICT CAP):
    - NO_VISUAL: Simple arithmetic, plain definitions, or calculations where a visual adds no value.
    - ABSOLUTE BLOCK: Do NOT convert text steps, mathematical calculations, or redundant graphics into SVG images unnecessarily.
    - Only generate visuals when it conceptually adds real learning value (e.g. circuits, geometry, forces, anatomy).

    CRITICAL LANGUAGE & SPOKEN NARRATION INSTRUCTIONS:
    1. CONTINUOUS AUDIO LANGUAGE MANDATE (CRITICAL):
       - You MUST write the 'audio_explanation' field STRICTLY, 100% COMPLETELY in ${language} from the very first word to the very last word.
       - NEVER switch, revert, or drop into English mid-sentence or in subsequent steps.
       - NEVER mix English sentences into 'audio_explanation' (only the fixed brand name "Professor X" must remain in English characters).
       - ALL OTHER FIELDS (subject, topic, question, board_content, reference_text, explanation, formulas, diagram_description, study notes, etc.) MUST ALWAYS BE IN ENGLISH ONLY.
       - DO NOT translate the JSON keys.
    2. SEPARATION OF VISUAL MATHEMATICS & SPOKEN NARRATION:
       - 'board_content' is the visual chalkboard: use formal, clean LaTeX expressions (e.g., $\cos(60^\circ) = \frac{1}{2}$, $\vec{F} = m\vec{a}$).
       - 'audio_explanation' and 'explanation' are the teacher's SPOKEN NARRATION:
         * NEVER output raw LaTeX syntax, backslash commands (e.g. do NOT write \\frac, \\circ, \\sqrt, \\cos, \\theta), or dollar signs ($) in 'audio_explanation' or 'explanation'.
         * Express mathematical formulas naturally as a teacher would speak them in ${language} (e.g. "cos 60 degrees", "1 by 2", "x squared", etc.).
         * This ensures TTS engines narrate smoothly without choking on backslashes or reading markup symbols.
    3. STEP-BY-STEP PROGRESSION & FORMULA ALIGNMENT:
       - Each step must represent one clear, focused derivation or conceptual milestone.
       - Do NOT dump complete multi-step derivations in a single frame. Progress systematically step-by-step.
       - The spoken narration in 'audio_explanation' must strictly explain and align with the specific formula or calculation shown on that step's 'board_content'.
    4. VISUAL REFERENCE TO DIAGRAM & REFERENCE LINES:
       - Whenever a visual is present (diagram_svg, image_url, or static_image_url), the narration in both 'explanation' and 'audio_explanation' MUST explicitly direct the student to look at that visual (e.g. "Notice in the diagram on the right...", "In the figure, observe...").
       - Provide a concise 'reference_text' (5-7 words, e.g. "Refer to angle $\theta$ in diagram") linking the mathematical step to the visual element.

    If the question is empty but an image is provided, extract the question from the image.
    
    Provide the output in JSON format with the following structure:
    - subject: The academic subject.
    - topic: The specific topic.
    - question: The full text of the question.
    - steps: An array of steps, where each step has:
      - step: The step number.
      - board_content: The core content for this step. Use LaTeX for math/science formulas. For coding, use code blocks or clear logic.
        FORMATTING RULES (MANDATORY):
        1. STRICT FRAME STEP BUDGET & LOGICAL TEACHING UNITS:
           - MAXIMUM 3 TO 4 LOGICAL TEACHING STEPS per teaching frame (fewer when required by measured space).
           - A single logical teaching step can naturally contain multiple lines, equations, definitions, or calculations as long as the complete measured content fits safely inside the teaching area.
           - If a solution or derivation has multiple distinct teaching concepts or extensive multi-part calculations, split them into sequential logical steps in the steps array.
        2. INTELLIGENT STRUCTURED EDUCATIONAL REPRESENTATIONS:
           - The AI must select the clearest, most educational representation for each step:
             * MATRICES & DETERMINANTS: When solving matrix operations (addition, multiplication, determinants, inverses, systems $A\vec{x} = \vec{b}$), use real LaTeX matrix environments: $\begin{pmatrix} a & b \\ c & d \end{pmatrix}$, $\begin{bmatrix} 1 & 2 \\ 3 & 4 \end{bmatrix}$, or $|A| = \begin{vmatrix} a & b \\ c & d \end{vmatrix}$. NEVER format matrices as flat text like $A = [1,2,3,4]$.
             * TABLES: When organizing given data, comparing cases, presenting time vs distance, quantity vs unit, or truth tables, use real Markdown tables:
               | Parameter | Value | Unit |
               |---|---|---|
               | Initial Velocity ($u$) | 0 | m/s |
               | Acceleration ($a$) | 9.8 | m/s$^2$ |
             * ALIGNED DERIVATIONS: For multi-step mathematical calculations and derivations, format them as aligned equations:
               $\begin{aligned} a^2 + b^2 &= c^2 \\ 3^2 + 4^2 &= c^2 \\ 9 + 16 &= c^2 \\ 25 &= c^2 \\ c &= 5\text{ units} \end{aligned}$
             * VECTORS & COORDINATES: Use vector notation $\vec{v} = \begin{pmatrix} v_x \\ v_y \end{pmatrix}$, $\vec{F}_{net} = m \vec{a}$, and $(x_1, y_1) = (2, 3)$.
             * CHEMICAL EQUATIONS: Format balanced reactions with state symbols: $\text{CH}_4\text{(g)} + 2\text{O}_2\text{(g)} \rightarrow \text{CO}_2\text{(g)} + 2\text{H}_2\text{O}\text{(l)}$.
        3. INLINE MATH & SYMBOL COHESION:
           - NEVER break an equation, variable definition, or unit across multiple lines. Text that genuinely fits on one line must remain on one line.
           - Keep entire equations cohesive: e.g. "• Rocket Acceleration $(a_r) = 2\text{ m/s}^2\ (+x)$"
           - NEVER isolate single variables (like $w$ or $\theta$), units (like "units" or "m/s"), or brackets on their own lines.
           - ALWAYS use single dollar signs $...$ for inline math within text so words and math flow naturally together.
           - Use block math $$...$$ ONLY for standalone, multi-line, or centerpiece matrix/fraction equations.
        4. TEXT FORMATTING & SPACING:
           - Ensure proper spacing between words (e.g., use 'Condition for non-zero' instead of 'Conditionfornon-zero').
           - Avoid unnecessary line breaks: Do NOT split short sentences into multiple lines. Do NOT break lines mid-phrase.
           - Implement word-safe wrapping: Never break a word in between.
        5. COMPLETE CONTENT SYNCHRONIZATION & MULTI-STEP INTEGRITY (STRICT):
           - CRITICAL RULE: EVERY SINGLE point, formula, example, note, paragraph, and explanation in the Study Notes MUST be fully represented in the video sequence without skipping, truncating, or omitting anything.
           - If a topic or section has multiple explanation points, formulas, or sub-steps (e.g. 3 or 4 points), organize them cleanly into sequential 'steps' in the steps array.
           - One Step = One Logical Bullet/Formula/Concept. You may use [BR] to separate sub-lines within a single logical 'board_content'.
           - Example: "Formula 1 text [BR] Formula 2 text [BR] Formula 3 text"
           - NEVER condense or drop teaching steps. Create as many steps as needed in the steps array so that 100% of the Study Notes content is taught in the video.
        6. TEXT STYLING: The current step text must always be WHITE. Maintain consistency across all frames. Apply the same color palette and styling rules in Study Notes as well.
      - reference_text: A small reference statement, formula, or rule (5-7 words). This will be displayed at the bottom of the board.
      - explanation: The full, detailed spoken explanation for this step in English. Use a calm, bold, and professional tone. Refer to the diagram if present. Do not use raw LaTeX markup.
      - audio_explanation: The same conversational teacher narration as 'explanation', but translated 100% into ${language}. Speak math words naturally without raw LaTeX backslashes or dollar signs.
      - diagram_svg: (Optional but HIGHLY RECOMMENDED if diagram_needed is true) A valid, raw SVG string. 
      - diagram_description: A comprehensive description of the current diagram (MAX 300 characters). It MUST explicitly state the core shapes, data points, or conditions shown.
    VISUAL REPRESENTATION — INTELLIGENT ENGINE V8:
    1. QUESTION CLASSIFICATION (MANDATORY GATE)
       Analyze the user input and classify its visual needs into:
       - MATH_PROBLEM: Requires coordinate systems, functions, geometry. (Use DYNAMIC/SVG).
       - REAL_TIME_PHYSICS: Requires force diagrams, circuits, optical paths. (Use DYNAMIC/SVG).
       - GENERALIZED_THEORY: Biology, Chemistry, Physics theory, Geography, History, CS, Engineering, Economics requiring textbook diagrams, anatomy, or molecular structures. (Use STATIC).
       - ALGORITHM/ENGINEERING: Flowcharts, tree structures, system architecture. (Use DYNAMIC/SVG or STATIC).
       - NO_VISUAL: Simple math or plain text theory where a visual adds no value.

    2. VISUAL TYPE SELECTION:
       - DYNAMIC: Use for MATH_PROBLEM and REAL_TIME_PHYSICS. Trigger valid 'diagram_svg'.
       - STATIC: Use for GENERALIZED_THEORY. You MUST provide concept-specific 'image_query' or 'static_image_query'.
       - NONE: Use for NO_VISUAL.

    3. EDUCATIONAL LABELED DIAGRAM SEARCH STRATEGY (STRICT):
       When generating 'image_query' or 'static_image_query' for static web visuals, formulate search terms directly from the SPECIFIC CONCEPT being taught in that step, targeting labeled educational diagrams:
       - BIOLOGY: "human heart structure labeled diagram", "chloroplast internal structure labeled", "plant cell organelles labeled diagram"
       - CHEMISTRY: "fractional distillation apparatus labeled diagram", "benzene ring orbital structure diagram", "atomic structure energy levels"
       - PHYSICS THEORY: "photoelectric effect apparatus labeled diagram", "refraction through prism ray diagram"
       - GEOGRAPHY: "subduction zone tectonics labeled diagram", "oxbow lake formation process diagram"
       - HISTORY: "ancient silk road trade routes map", "indus valley civilization city plan map"
       - COMPUTER SCIENCE: "von neumann architecture components diagram", "binary search tree traversal diagram"
       - ENGINEERING: "four stroke diesel engine mechanism labeled diagram", "operational amplifier internal circuit diagram"
       - ECONOMICS: "supply and demand equilibrium curve diagram", "circular flow of income model diagram"

       SEARCH PRIORITY ORDER:
       1) Labeled educational diagram
       2) Segmented/annotated educational illustration
       3) Concept-specific scientific/technical diagram
       4) High-quality educational illustration
       5) Relevant real-world image only when it genuinely improves understanding

       NEVER generate generic photo terms (e.g., "heart photo", "person in lab", "landscape photo"). Focus strictly on the exact concept name with labeled diagram intent.

    4. CONTEXTUAL NARRATION FOR VISUALS:
       Whenever an image or diagram is present, the 'explanation' and 'audio_explanation' MUST explicitly guide the student on what specific structures, labels, or features to observe in the visual panel on the right.

    5. ABSOLUTE ACCURACY RULES:
       - Diagrams (SVG or Static) MUST be conceptually correct.
       - Proper labeling is mandatory.
       - Scale and proportions must be accurate to the data provided.

    5. STRICT VISUAL RULES (GLOBAL):
       - NO TEXT DUMPING: No long explanations or formula repetition inside SVG.
       - MINIMAL LABELS ONLY: Axis labels (x, y), Component names (Heart, CPU, Node), Short identifiers (Input, Step 1).
       - ACCURACY IS MANDATORY: Diagrams must be conceptually and mathematically correct.
       - NO OVERLAPPING TEXT: All text labels must have clear spacing and not intersect with lines, shapes, or other text.
       - LAYER PRIORITY: All text MUST be rendered on the topmost layer (last in the SVG code) to ensure visibility.
       - NO ANIMATIONS: All elements MUST be static.

    6. ABSOLUTE BLOCK CONDITIONS:
       - Reject if it only shows text, equations, or answers.
       - Reject if it is vague, symbolic, or purely decorative.
       - If unsure, DO NOT generate a visual.

    SVG TECHNICAL REQUIREMENTS (STRICT):
        1. Use SINGLE QUOTES for all SVG attributes.
        2. VIEWBOX: Use viewBox='0 0 400 300'. 
        3. ACCURACY & SCALE: Diagrams MUST be proportional. If a problem states "20N force" and "10N force", the 20N arrow must be accurately twice as long as the 10N arrow. Circuits must be closed and logical.
        4. STYLING: 
           - Grid lines (stroke='#334155', stroke-width='0.5', stroke-dasharray='4') for context.
           - Axes (stroke='#ffffff', stroke-width='2').
           - Primary Elements (Bright contrast: #3b82f6 (blue), #ef4444 (red), #22c55e (green), #f59e0b (gold)).
        5. LABELS & ANNOTATIONS: 
           - Use <text> tags, White (#ffffff), font-size 12px or 14px.
           - Align labels clearly with their components. 
           - Use leader lines (lines connecting label to component) if necessary for clarity in complex diagrams.
           - Include units (kg, m/s, Ohm, V, etc.) in labels.
        6. SYMBOLS: Use the "PHYSICS & TECHNICAL SYMBOLS" rules strictly for all technical diagrams.
    
    CONCLUSION STEP (MANDATORY):
    - The VERY LAST step in the 'steps' array MUST be a conclusion.
    - 'board_content' for this step should show the final result or answer clearly.
    - 'explanation' and 'audio_explanation' for this step MUST include a clear summary of the final answer and key concept.
    - Concluding Call-to-Action Mandate:
      * 'explanation' (English) MUST end with: "If you have any doubts, use the doubt box below. For more questions, ask Professor X."
      * 'audio_explanation' (${language}) MUST translate this concluding sentence 100% naturally into ${language} while preserving the English brand name "Professor X". NEVER leave the concluding sentence in English when ${language} is not English! (Example for Hindi: "यदि आपके कोई संदेह हैं, तो नीचे दिए गए डाउट बॉक्स का उपयोग करें। अधिक प्रश्नों के लिए, Professor X से पूछें।", Example for Marathi: "जर तुम्हाला काही शंका असतील तर खालील डाउट बॉक्स वापरा. अधिक प्रश्नांसाठी, Professor X ला विचारा.").
    
    - formulas: An array of key formulas or concepts used (in LaTeX).
    - diagram_needed: (Boolean) MANDATORY. 
      - Set TRUE if visual_type is 'DYNAMIC' or 'STATIC'.
      - DECISION GATE: Ask "Will a visual/graph/reference diagram significantly improve understanding of this specific concept?"
      - ALWAYS set TRUE for Biology, Chemistry, anatomy, or complex machines.
    - diagram_description: A brief description of the visual needed.
    - final_answer: The final conclusion or answer.
    - visual_type: "DYNAMIC" | "STATIC" | "NONE".
    - static_image_query: (Required ONLY if visual_type is STATIC) A precise educational image search query for Wikimedia.
    - image_keywords: (Required ONLY if visual_type is STATIC) An array of 3-5 critical keywords to validate the image search result.
    `;

  let retries = 2;
  while (retries >= 0) {
    try {
      const response = await callGemini(
        "gemini-3-flash-preview", 
        [{ role: 'user', parts }],
        systemInstruction,
        "application/json"
      );

      const text = response.text;
      if (!text) {
        throw new Error("No response from AI");
      }
      
      const parsed = parseAndCleanJSON(text);
      return await resolveStepAndLessonImages(parsed);
    } catch (error: any) {
      console.error(`Error generating solution (retries left: ${retries}):`, error);
      const errMsg = (error?.message || "").toLowerCase();
      const isAuthError = errMsg.includes("session expired") || errMsg.includes("authentication required") || errMsg.includes("unauthenticated") || errMsg.includes("401");
      const isBadRequest = errMsg.includes("400") || errMsg.includes("invalid argument");
      if (isAuthError || isBadRequest || retries === 0) {
        throw error;
      }
      retries--;
      // Wait with backoff and jitter before retrying
      const waitTime = 1200 + Math.floor(Math.random() * 800);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  throw new Error("Failed to generate solution after retries");
}
